import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import ProtocolSelector from './ProtocolSelector'

const WidgetEditModal = ({
  isOpen,
  onClose,
  onSave,
  widget = null,
  index = -1
}) => {
  const [customFields, setCustomFields] = useState([])
  const [fieldIdCounter, setFieldIdCounter] = useState(0)
  const [showCustomFieldsHelp, setShowCustomFieldsHelp] = useState(false)

  // OpenMeteo 地理定位状态
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [citySearch, setCitySearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [showSearchResults, setShowSearchResults] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm()

  const watchedType = watch('type')
  const watchedProvider = watch('provider')
  const watchedSelectedMetric = watch('selectedMetric')

  // OpenMeteo 地理定位功能
  const getCurrentLocation = async () => {
    setIsGettingLocation(true)
    try {
      const response = await fetch('/api/location/current')
      const result = await response.json()

      if (result.success) {
        setValue('latitude', result.data.latitude)
        setValue('longitude', result.data.longitude)
        setValue('location', `${result.data.city}, ${result.data.country}`)
        alert(`已获取当前位置: ${result.data.city}, ${result.data.country}`)
      } else {
        alert('获取当前位置失败: ' + result.error)
      }
    } catch (error) {
      console.error('获取位置错误:', error)
      alert('获取位置失败: ' + error.message)
    } finally {
      setIsGettingLocation(false)
    }
  }

  const searchCity = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }

    setIsSearching(true)
    try {
      const response = await fetch(`/api/location/search?q=${encodeURIComponent(query)}`)
      const result = await response.json()

      if (result.success) {
        setSearchResults(result.data.slice(0, 5))
        setShowSearchResults(true)
      } else {
        console.error('城市搜索失败:', result.error)
        setSearchResults([])
        setShowSearchResults(false)
      }
    } catch (error) {
      console.error('城市搜索错误:', error)
      setSearchResults([])
      setShowSearchResults(false)
    } finally {
      setIsSearching(false)
    }
  }

  const selectSearchResult = (result) => {
    setValue('latitude', result.latitude)
    setValue('longitude', result.longitude)
    setValue('location', result.city || result.display_name.split(',')[0])
    setCitySearch('')
    setSearchResults([])
    setShowSearchResults(false)
  }

  const handleCitySearchChange = (e) => {
    const value = e.target.value
    setCitySearch(value)
    searchCity(value)
  }

  // 🎯 判断是否显示链接配置
  const shouldShowLinkConfig = (type) => {
    return type && type !== 'search'
  }

  // 🎯 渲染类型特定的配置字段
  const renderTypeSpecificConfig = (type) => {
    // 精简后的小部件配置渲染 - 只支持基本类型
    switch (type) {
      case 'resources':
        return renderResourcesConfig()
      case 'search':
        return renderSearchConfig()
      case 'datetime':
        return renderDateTimeConfig()
      case 'greeting':
        return renderGreetingConfig()
      case 'logo':
        return renderLogoConfig()
      case 'openmeteo':
        return renderOpenMeteoConfig()
      case 'customapi':
        return renderCustomApiConfig()
      default:
        return null
    }
  }

  // 🎯 系统资源配置（符合官方文档）
  const renderResourcesConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">📊 系统资源配置</h5>

      {/* 分组标签与展开 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">分组标签（label）</label>
          <input {...register('label')} className="input" placeholder="如：System / Storage" />
        </div>
        <label className="flex items-center mt-6 md:mt-8">
          <input {...register('expanded')} type="checkbox" className="mr-2" />
          <span className="text-sm text-gray-300">展开更多详情（expanded）</span>
        </label>
      </div>

      {/* 资源勾选 */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <label className="flex items-center">
          <input {...register('cpu')} type="checkbox" className="mr-2" />
          <span className="text-sm text-gray-300">CPU</span>
        </label>
        <label className="flex items-center">
          <input {...register('memory')} type="checkbox" className="mr-2" />
          <span className="text-sm text-gray-300">内存（memory）</span>
        </label>
        <label className="flex items-center">
          <input {...register('cputemp')} type="checkbox" className="mr-2" />
          <span className="text-sm text-gray-300">CPU 温度（cputemp）</span>
        </label>
        <label className="flex items-center">
          <input {...register('uptime')} type="checkbox" className="mr-2" />
          <span className="text-sm text-gray-300">运行时间（uptime）</span>
        </label>
        <label className="flex items-center">
          <input {...register('load')} type="checkbox" className="mr-2" />
          <span className="text-sm text-gray-300">系统负载（load）</span>
        </label>
      </div>

      {/* 磁盘配置：支持单路径或多路径（换行分隔） */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">磁盘路径（disk）</label>
        <textarea
          {...register('diskInput')}
          className="input min-h-[90px]"
          placeholder="/mnt/storage\n/mnt/backup\n/mnt/media"
        />
        <p className="mt-1 text-xs text-gray-400">支持单路径或多路径（每行一个）。对应 Homepage 的 disk 可为字符串或数组。</p>
      </div>

      {/* 网络配置：启用 + 可选接口名 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center">
          <input {...register('enableNetwork')} type="checkbox" className="mr-2" />
          <span className="text-sm text-gray-300">网络（network）</span>
        </label>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">网络接口名称（可选）</label>
          <input {...register('networkInterface')} className="input" placeholder="留空=默认接口，例如 eth0" />
        </div>
      </div>

      {/* 温度单位与范围，仅用于 CPU 温度 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">温度单位（units）</label>
          <select {...register('units')} className="select">
            <option value="metric">metric（摄氏度）</option>
            <option value="imperial">imperial（华氏度）</option>
          </select>
          <p className="mt-1 text-xs text-gray-400">仅对 CPU 温度生效。</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">最低温（tempmin）</label>
          <input {...register('tempmin')} className="input" type="number" placeholder="0" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">最高温（tempmax）</label>
          <input {...register('tempmax')} className="input" type="number" placeholder="100" />
        </div>
      </div>

      {/* 刷新与单位 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">刷新间隔（ms, refresh）</label>
          <input {...register('refresh')} className="input" type="number" placeholder="3000" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">磁盘容量单位（diskUnits）</label>
          <select {...register('diskUnits')} className="select">
            <option value="bytes">bytes（默认）</option>
            <option value="bbytes">bbytes（二进制字节）</option>
          </select>
        </div>
      </div>

      <div className="bg-gray-800 p-3 rounded border border-gray-700 text-xs text-gray-400">
        注意：systeminformation 对部分场景有限制（如 BTRFS RAID 磁盘使用），CPU 温度在部分系统上可能无法获取。如需更全面的主机统计可考虑使用 Glances 小部件。
      </div>
    </div>
  )

  // 🎯 搜索框配置
  const renderSearchConfig = () => {
    // 使用稳定的条件判断
    const showCustomConfig = watchedProvider === 'custom'

    return (
      <div className="space-y-4">
        <h5 className="font-medium text-gray-300">🔍 搜索配置</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">搜索提供商</label>
            <select {...register('provider')} className="select">
              {/* Homepage官方支持的搜索提供商 */}
              <option value="duckduckgo">DuckDuckGo（默认）</option>
              <option value="google">Google</option>
              <option value="bing">Bing</option>
              <option value="baidu">百度</option>
              <option value="brave">Brave Search</option>
              <option value="custom">自定义</option>
            </select>
            <p className="mt-1 text-xs text-gray-400">ℹ️ 如需多个提供商，请开启下方“多提供商模式”。注意：多提供商模式不支持“自定义”。</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">打开方式</label>
            <select {...register('target')} className="select">
              <option value="_blank">_blank（新窗口）</option>
              <option value="_self">_self（当前窗口）</option>
              <option value="_parent">_parent</option>
              <option value="_top">_top</option>
            </select>
          </div>
          <div className="col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('showSearchSuggestions')}
                  className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                />
                <span className="text-sm text-gray-300">显示搜索建议</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('focus')}
                  className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                />
                <span className="text-sm text-gray-300">自动聚焦搜索框</span>
              </label>
            </div>
          </div>
        </div>

        {/* 多提供商模式（不包含 custom） */}
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-medium text-gray-200">多提供商模式</h4>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" {...register('enableMultiProviders')} className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800" />
              <span className="text-sm text-gray-300">启用</span>
            </label>
          </div>
          <p className="text-xs text-gray-400 mb-3">启用后可选择多个内置提供商（google, duckduckgo, bing, baidu, brave）。该模式与“自定义”互斥。</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" {...register('providers_duckduckgo')} className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800" />
              <span className="text-sm text-gray-300">DuckDuckGo</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" {...register('providers_google')} className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800" />
              <span className="text-sm text-gray-300">Google</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" {...register('providers_bing')} className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800" />
              <span className="text-sm text-gray-300">Bing</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" {...register('providers_baidu')} className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800" />
              <span className="text-sm text-gray-300">百度</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" {...register('providers_brave')} className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800" />
              <span className="text-sm text-gray-300">Brave</span>
            </label>
          </div>
        </div>

        {/* 自定义搜索引擎URL配置 */}
        {showCustomConfig && (
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
            <h4 className="text-lg font-medium text-gray-200 mb-4">自定义搜索引擎</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">搜索 URL 模板</label>
                <input
                  {...register('customUrl')}
                  className="input"
                  placeholder="例如：https://www.ecosia.org/search?q="
                />
                <p className="mt-1 text-xs text-gray-400">提供基础 URL，系统将自动在末尾追加搜索关键词。</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">建议接口 URL（可选）</label>
                <input
                  {...register('suggestionUrl')}
                  className="input"
                  placeholder="例如：https://ac.ecosia.org/autocomplete?type=list&q="
                />
                <p className="mt-1 text-xs text-gray-400">返回格式需为 [query, [suggestions...]]</p>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // 🎯 OpenMeteo天气配置
  const renderOpenMeteoConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">🌤️ OpenMeteo 天气配置</h5>

      {/* 位置获取区域 */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h6 className="text-sm font-medium text-gray-300 mb-3">📍 位置获取</h6>
        <div className="space-y-3">
          {/* 当前位置按钮 */}
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={isGettingLocation}
            className="btn-secondary w-full flex items-center justify-center"
          >
            {isGettingLocation ? (
              <>
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                获取中...
              </>
            ) : (
              <>
                🌐 获取当前位置
              </>
            )}
          </button>

          {/* 城市搜索 */}
          <div className="relative">
            <input
              type="text"
              value={citySearch}
              onChange={handleCitySearchChange}
              className="input w-full"
              placeholder="输入城市名称搜索..."
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* 搜索结果 */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => selectSearchResult(result)}
                    className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                  >
                    <div className="font-medium">{result.display_name}</div>
                    <div className="text-xs text-gray-400">
                      {result.latitude.toFixed(4)}, {result.longitude.toFixed(4)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 经纬度配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">纬度 *</label>
          <input {...register('latitude', { required: '纬度是必填的' })} className="input" type="number" step="any" placeholder="39.9042" />
          {errors.latitude && <p className="mt-1 text-sm text-red-400">{errors.latitude.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">经度 *</label>
          <input {...register('longitude', { required: '经度是必填的' })} className="input" type="number" step="any" placeholder="116.4074" />
          {errors.longitude && <p className="mt-1 text-sm text-red-400">{errors.longitude.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">城市名称</label>
          <input {...register('location')} className="input" placeholder="北京" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">单位</label>
          <select {...register('units')} className="select">
            <option value="metric">公制 (℃, km/h)</option>
            <option value="imperial">英制 (℉, mph)</option>
          </select>
        </div>
      </div>
    </div>
  )

  // 🎯 日期时间配置
  const renderDateTimeConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">📅 日期时间配置</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">文字大小</label>
          <select {...register('text_size')} className="select">
            <option value="4xl">4xl (最大)</option>
            <option value="3xl">3xl</option>
            <option value="2xl">2xl</option>
            <option value="xl">xl</option>
            <option value="md">md</option>
            <option value="sm">sm</option>
            <option value="xs">xs (最小)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Locale（本地化）</label>
          <input {...register('locale')} className="input" placeholder="例如：zh-CN、en-US、nl" />
          <p className="mt-1 text-xs text-gray-400">留空使用浏览器/系统默认区域设置</p>
        </div>
      </div>

      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h6 className="text-sm font-medium text-gray-300 mb-3">格式（Intl.DateTimeFormat）</h6>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">dateStyle（日期样式）</label>
            <select {...register('dateStyle')} className="select">
              <option value="">（不设置）</option>
              <option value="short">short（短）</option>
              <option value="medium">medium（中）</option>
              <option value="long">long（长）</option>
              <option value="full">full（完整）</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">timeStyle（时间样式）</label>
            <select {...register('timeStyle')} className="select">
              <option value="">（不设置）</option>
              <option value="short">short（短）</option>
              <option value="medium">medium（中）</option>
              <option value="long">long（长）</option>
              <option value="full">full（完整）</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <input type="checkbox" {...register('hour12')} className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800" />
            <span className="text-sm text-gray-300">hour12（12 小时制）</span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">hourCycle（小时周期）</label>
            <select {...register('hourCycle')} className="select">
              <option value="">（不设置）</option>
              <option value="h11">h11（0-11，12h制）</option>
              <option value="h12">h12（1-12，12h制）</option>
              <option value="h23">h23（0-23，24h制）</option>
              <option value="h24">h24（1-24，24h制）</option>
            </select>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-400">以上选项会直接传给 Intl.DateTimeFormat</p>
      </div>
    </div>
  )

  // 🎯 问候语配置
  const renderGreetingConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">👋 问候语配置</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">问候文字</label>
          <input {...register('text')} className="input" placeholder="Hello!" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">字体尺寸</label>
          <select {...register('text_size')} className="select">
            <option value="4xl">4xl (特大)</option>
            <option value="3xl">3xl (大)</option>
            <option value="2xl">2xl (中等)</option>
            <option value="xl">xl (正常)</option>
            <option value="md">md (小)</option>
            <option value="sm">sm (较小)</option>
            <option value="xs">xs (最小)</option>
          </select>
        </div>
      </div>
    </div>
  )

  // 🎯 Glances 配置
  const renderGlancesConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">📈 Glances 配置</h5>

      {/* 基础配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">URL *</label>
          <input {...register('url', { required: 'URL是必填的' })} className="input" placeholder="http://localhost:61208" />
          {errors.url && <p className="mt-1 text-sm text-red-400">{errors.url.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">API 版本</label>
          <select {...register('version')} className="select" defaultValue="4">
            <option value="3">版本 3</option>
            <option value="4">版本 4 (推荐)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">用户名</label>
          <input {...register('username')} className="input" placeholder="admin" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">密码</label>
          <input {...register('password')} type="text" className="input" />
        </div>
      </div>

      {/* 监控指标选择 */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h4 className="text-lg font-medium text-gray-200 mb-4">监控配置</h4>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">显示指标 <span className="text-xs text-gray-400">(可取消单选)</span></label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50' : ''}`}>
              <input
                type="radio"
                name="metric"
                value="cpu"
                checked={watchedSelectedMetric === 'cpu'}
                onClick={() => {
                  const currentValue = watchedSelectedMetric
                  if (currentValue === 'cpu') {
                    setValue('selectedMetric', '')
                  } else {
                    setValue('selectedMetric', 'cpu')
                  }
                }}
                disabled={customFields.length > 0}
                className="border-gray-600 text-blue-600"
              />
              <span className="text-sm text-gray-300">CPU 使用率</span>
            </label>
            <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50' : ''}`}>
              <input
                type="radio"
                name="metric"
                value="memory"
                checked={watchedSelectedMetric === 'memory'}
                onClick={() => {
                  const currentValue = watchedSelectedMetric
                  if (currentValue === 'memory') {
                    setValue('selectedMetric', '')
                  } else {
                    setValue('selectedMetric', 'memory')
                  }
                }}
                disabled={customFields.length > 0}
                className="border-gray-600 text-blue-600"
              />
              <span className="text-sm text-gray-300">内存使用</span>
            </label>
            <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50' : ''}`}>
              <input
                type="radio"
                name="metric"
                value="info"
                checked={watchedSelectedMetric === 'info'}
                onClick={() => {
                  const currentValue = watchedSelectedMetric
                  if (currentValue === 'info') {
                    setValue('selectedMetric', '')
                  } else {
                    setValue('selectedMetric', 'info')
                  }
                }}
                disabled={customFields.length > 0}
                className="border-gray-600 text-blue-600"
              />
              <span className="text-sm text-gray-300">系统信息</span>
            </label>
            <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50' : ''}`}>
              <input
                type="radio"
                name="metric"
                value="process"
                checked={watchedSelectedMetric === 'process'}
                onClick={() => {
                  const currentValue = watchedSelectedMetric
                  if (currentValue === 'process') {
                    setValue('selectedMetric', '')
                  } else {
                    setValue('selectedMetric', 'process')
                  }
                }}
                disabled={customFields.length > 0}
                className="border-gray-600 text-blue-600"
              />
              <span className="text-sm text-gray-300">进程列表</span>
            </label>
            <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50' : ''}`}>
              <input
                type="radio"
                name="metric"
                value="containers"
                checked={watchedSelectedMetric === 'containers'}
                onClick={() => {
                  const currentValue = watchedSelectedMetric
                  if (currentValue === 'containers') {
                    setValue('selectedMetric', '')
                  } else {
                    setValue('selectedMetric', 'containers')
                  }
                }}
                disabled={customFields.length > 0}
                className="border-gray-600 text-blue-600"
              />
              <span className="text-sm text-gray-300">容器列表</span>
            </label>
            <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50' : ''}`}>
              <input
                type="radio"
                name="metric"
                value="network"
                checked={watchedSelectedMetric === 'network'}
                onClick={() => {
                  const currentValue = watchedSelectedMetric
                  if (currentValue === 'network') {
                    setValue('selectedMetric', '')
                  } else {
                    setValue('selectedMetric', 'network')
                  }
                }}
                disabled={customFields.length > 0}
                className="border-gray-600 text-blue-600"
              />
              <span className="text-sm text-gray-300">网络流量</span>
            </label>
          </div>
        </div>

        {/* 自定义字段 */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <label className="block text-sm font-medium text-gray-300">自定义监控字段</label>
              <button
                type="button"
                onClick={() => setShowCustomFieldsHelp(!showCustomFieldsHelp)}
                className="w-5 h-5 bg-blue-600 text-white text-xs rounded-full hover:bg-blue-700 transition-colors flex items-center justify-center"
                title="查看帮助文档"
              >
                ?
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                setCustomFields([...customFields, { id: Date.now(), field: '', label: '' }])
                setValue('selectedMetric', '')
              }}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              + 添加字段
            </button>
          </div>

          {customFields.length > 0 && (
            <div className="space-y-3">
              {customFields.map((customField, index) => (
                <div key={customField.id} className="flex items-center space-x-3 p-3 bg-gray-900 rounded border border-gray-600">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="字段名 (如: sensor:Package id 0)"
                      value={customField.field}
                      onChange={(e) => {
                        const newFields = [...customFields]
                        newFields[index].field = e.target.value
                        setCustomFields(newFields)
                      }}
                      className="input text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="显示名称 (如: CPU温度)"
                      value={customField.label}
                      onChange={(e) => {
                        const newFields = [...customFields]
                        newFields[index].label = e.target.value
                        setCustomFields(newFields)
                      }}
                      className="input text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newFields = customFields.filter((_, i) => i !== index)
                      setCustomFields(newFields)
                    }}
                    className="px-2 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  >
                    删除
                  </button>
                </div>
              ))}
            </div>
          )}

          {customFields.length === 0 && (
            <div className="text-center py-4 text-gray-400 text-sm border border-dashed border-gray-600 rounded">
              点击"添加字段"来添加自定义监控指标
              <br />
              <span className="text-xs text-gray-500">
                例如：sensor:Package id 0 (CPU温度)、disk:sda (磁盘I/O) 等
              </span>
            </div>
          )}
        </div>

        {/* 帮助文档 */}
        {showCustomFieldsHelp && (
          <div className="mt-4 p-4 bg-blue-900/20 border border-blue-600/30 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-300 mb-3">📖 自定义监控字段帮助文档</h4>

            <div className="space-y-4 text-sm text-gray-300">
              <div>
                <h5 className="font-medium text-blue-200 mb-2">📊 基础监控字段</h5>
                <div className="space-y-1 text-xs">
                  <div><code className="bg-gray-800 px-1 rounded">info</code> - 系统信息概览（主机名、OS、内核版本、CPU类型等）</div>
                  <div><code className="bg-gray-800 px-1 rounded">cpu</code> - CPU 使用率</div>
                  <div><code className="bg-gray-800 px-1 rounded">memory</code> - 内存使用情况</div>
                  <div><code className="bg-gray-800 px-1 rounded">process</code> - 前5个进程（按CPU使用率）</div>
                  <div><code className="bg-gray-800 px-1 rounded">containers</code> - Docker容器列表</div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-blue-200 mb-2">🌡️ 温度传感器</h5>
                <div className="space-y-1 text-xs">
                  <div><code className="bg-gray-800 px-1 rounded">sensor:Package id 0</code> - CPU整体温度</div>
                  <div><code className="bg-gray-800 px-1 rounded">sensor:Core 0</code> - CPU核心0温度</div>
                  <div><code className="bg-gray-800 px-1 rounded">sensor:it8613 0</code> - 主板传感器温度</div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-blue-200 mb-2">💾 磁盘监控</h5>
                <div className="space-y-1 text-xs">
                  <div><code className="bg-gray-800 px-1 rounded">disk:sda</code> - 磁盘sda的I/O数据</div>
                  <div><code className="bg-gray-800 px-1 rounded">fs:/</code> - 根分区磁盘使用情况</div>
                  <div><code className="bg-gray-800 px-1 rounded">fs:/home</code> - /home分区磁盘使用情况</div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-blue-200 mb-2">🌐 网络监控</h5>
                <div className="space-y-1 text-xs">
                  <div><code className="bg-gray-800 px-1 rounded">network:eth0</code> - 以太网接口流量</div>
                  <div><code className="bg-gray-800 px-1 rounded">network:enp0s25</code> - 特定网络接口流量</div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-blue-200 mb-2">🎮 GPU监控</h5>
                <div className="space-y-1 text-xs">
                  <div><code className="bg-gray-800 px-1 rounded">gpu:0</code> - 第一个GPU使用情况</div>
                </div>
              </div>

              <div className="pt-2 border-t border-blue-600/30">
                <h5 className="font-medium text-yellow-300 mb-2">💡 使用提示</h5>
                <ul className="space-y-1 text-xs text-gray-400">
                  <li>• <strong>字段名</strong>：填写Glances API中的确切字段名</li>
                  <li>• <strong>显示名称</strong>：填写用户友好的中文名称</li>
                  <li>• 可访问 <code className="bg-gray-800 px-1 rounded">http://your-glances-url/api/4/sensors</code> 查看可用传感器</li>
                  <li>• Homepage小部件只显示第一个选中的监控指标</li>
                  <li>• 如需显示多个指标，请创建多个小部件</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* 高级配置 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">磁盘分区</label>
            <input {...register('disk')} className="input" placeholder="/ (默认根分区)" />
            <p className="mt-1 text-xs text-gray-400">指定要监控的磁盘分区，如 /home, /var 等</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">网络接口</label>
            <input {...register('interface')} className="input" placeholder="eth0 (默认主网卡)" />
            <p className="mt-1 text-xs text-gray-400">指定要监控的网络接口，如 eth0, ens33, wlan0 等</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">刷新间隔 (毫秒)</label>
            <input {...register('refreshInterval')} type="number" className="input" placeholder="5000" />
            <p className="mt-1 text-xs text-gray-400">数据刷新间隔，建议 5000-30000 毫秒</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">数据点限制</label>
            <input {...register('pointsLimit')} type="number" className="input" placeholder="15" />
            <p className="mt-1 text-xs text-gray-400">图表显示的数据点数量，默认15</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">磁盘单位</label>
            <select {...register('diskUnits')} className="select" defaultValue="bytes">
              <option value="bytes">字节 (bytes)</option>
              <option value="bbytes">二进制字节 (bbytes)</option>
            </select>
            <p className="mt-1 text-xs text-gray-400">磁盘容量显示单位</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">图表显示</label>
            <select {...register('chart')} className="select" defaultValue="true">
              <option value="true">显示图表</option>
              <option value="false">紧凑模式</option>
            </select>
            <p className="mt-1 text-xs text-gray-400">选择是否显示图表或使用紧凑模式</p>
          </div>
        </div>
      </div>
    </div>
  )

  // 🎯 Bilibili 配置
  const renderBilibiliConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">📺 Bilibili 视频数据配置</h5>

      {/* 说明提示 */}
      <div className="bg-pink-900/20 border border-pink-500/30 rounded-lg p-3">
        <div className="flex items-start space-x-2">
          <div className="w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-white text-xs">B</span>
          </div>
          <div className="text-sm text-pink-200">
            <p className="font-medium mb-1">Bilibili 视频数据监控说明</p>
            <p className="text-pink-300">
              监控指定 Bilibili 视频的观看数、点赞数、收藏数、投币数等数据。
              请输入视频的 BV 号或 AV 号。
            </p>
          </div>
        </div>
      </div>

      {/* 基础配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">视频 ID *</label>
          <input
            {...register('videoId', { required: 'Bilibili 视频 ID 是必填的' })}
            className="input"
            placeholder="BV1JFvNzHEDQ 或 av170001"
          />
          {errors.videoId && <p className="mt-1 text-sm text-red-400">{errors.videoId.message}</p>}
          <p className="mt-1 text-xs text-gray-400">
            支持 BV 号（如：BV1JFvNzHEDQ）或 AV 号（如：av170001）
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">API 基础地址</label>
          <input
            {...register('apiBaseUrl')}
            className="input"
            placeholder="http://配置管理器地址[:端口]"
          />
          <p className="mt-1 text-xs text-gray-400">
            配置管理器的访问地址，留空将自动检测
          </p>
        </div>
      </div>

      {/* 刷新间隔 */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">刷新间隔 (分钟)</label>
        <input
          {...register('refreshInterval', { valueAsNumber: true })}
          type="number"
          min="1"
          max="1440"
          className="input w-full md:w-1/2"
          placeholder="30"
        />
        <p className="mt-1 text-xs text-gray-400">
          数据刷新间隔，建议 30-60 分钟（避免频繁请求）
        </p>
      </div>

      {/* 显示数据选择 */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          显示数据 <span className="text-xs text-gray-400">(可多选)</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('showView')}
              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">观看数</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('showLike')}
              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">点赞数</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('showFavorite')}
              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">收藏数</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('showCoin')}
              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">投币数</span>
          </label>
        </div>
      </div>
    </div>
  )

  // 简化的其他配置函数
  const renderLogoConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">🖼️ Logo 配置</h5>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Logo 图片路径 *</label>
        <input {...register('icon', { required: 'Logo 图片路径是必填的' })} className="input" />
        {errors.icon && <p className="mt-1 text-sm text-red-400">{errors.icon.message}</p>}
      </div>
    </div>
  )


  const renderBookmarksConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">📖 书签配置</h5>
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
        <p className="text-sm text-yellow-200">
          💡 书签小部件主要通过自定义字段配置。
        </p>
      </div>
    </div>
  )

  // 🎯 EMBY 配置
  const renderEmbyConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">📺 EMBY 媒体服务器配置</h5>

      {/* 基础配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">服务器地址 *</label>
          <input {...register('url', { required: 'EMBY 服务器地址是必填的' })} className="input" placeholder="http://localhost:8096" />
          {errors.url && <p className="mt-1 text-sm text-red-400">{errors.url.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">API 密钥 *</label>
          <input {...register('key', { required: 'API 密钥是必填的' })} className="input" placeholder="your-api-key" />
          {errors.key && <p className="mt-1 text-sm text-red-400">{errors.key.message}</p>}
        </div>
      </div>

      {/* 功能开关配置 */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h4 className="text-lg font-medium text-gray-200 mb-4">功能配置</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('_nativeEmbyWidget')}
              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">启用原生 EMBY 小部件</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableBlocks')}
              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">启用块状显示</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableNowPlaying')}
              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示正在播放</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableUser')}
              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示用户信息</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableMediaControl')}
              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">启用媒体控制</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('showEpisodeNumber')}
              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示剧集编号</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('expandOneStreamToTwoRows')}
              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">单流扩展为两行</span>
          </label>
        </div>
      </div>

      {/* 配置说明 */}
      <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-green-300 mb-3">📖 EMBY 小部件配置说明</h4>
        <div className="space-y-2 text-sm text-green-200">
          <p><strong>原生 EMBY 小部件</strong>：启用 Homepage 的原生 EMBY 小部件支持</p>
          <p><strong>块状显示</strong>：以块状布局显示媒体信息（默认关闭）</p>
          <p><strong>正在播放</strong>：显示当前正在播放的媒体（默认开启）</p>
          <p><strong>用户信息</strong>：显示用户相关信息（默认关闭）</p>
          <p><strong>媒体控制</strong>：启用播放控制按钮（默认开启）</p>
          <p><strong>剧集编号</strong>：显示电视剧的剧集编号（默认关闭）</p>
          <p><strong>单流扩展</strong>：将单个流媒体扩展为两行显示（默认开启）</p>
        </div>
      </div>
    </div>
  )

  // 🎯 Jellyfin 配置
  const renderJellyfinConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">🐙 Jellyfin 媒体服务器配置</h5>

      {/* 基础配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">服务器地址 *</label>
          <input {...register('url', { required: 'Jellyfin 服务器地址是必填的' })} className="input" placeholder="http://localhost:8096" />
          {errors.url && <p className="mt-1 text-sm text-red-400">{errors.url.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">API 密钥 *</label>
          <input {...register('key', { required: 'API 密钥是必填的' })} className="input" placeholder="your-api-key" />
          {errors.key && <p className="mt-1 text-sm text-red-400">{errors.key.message}</p>}
        </div>
      </div>

      {/* 功能开关配置 */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h4 className="text-lg font-medium text-gray-200 mb-4">功能配置</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableBlocks')}
              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">启用块状显示</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableNowPlaying')}
              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示正在播放</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableUser')}
              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示用户信息</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableMediaControl')}
              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">启用媒体控制</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('showEpisodeNumber')}
              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示剧集编号</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('expandOneStreamToTwoRows')}
              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">单流扩展为两行</span>
          </label>
        </div>
      </div>

      {/* 配置说明 */}
      <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-purple-300 mb-3">📖 Jellyfin 小部件配置说明</h4>
        <div className="space-y-2 text-sm text-purple-200">
          <p><strong>块状显示</strong>：以块状布局显示媒体信息（默认关闭）</p>
          <p><strong>正在播放</strong>：显示当前正在播放的媒体（默认开启）</p>
          <p><strong>用户信息</strong>：显示用户相关信息（默认关闭）</p>
          <p><strong>媒体控制</strong>：启用播放控制按钮（默认开启）</p>
          <p><strong>剧集编号</strong>：显示电视剧的剧集编号（默认关闭）</p>
          <p><strong>单流扩展</strong>：将单个流媒体扩展为两行显示（默认开启）</p>
        </div>
      </div>
    </div>
  )

  // 🎯 Plex 配置
  const renderPlexConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">🎬 Plex 媒体服务器配置</h5>

      {/* 基础配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">服务器地址 *</label>
          <input {...register('url', { required: 'Plex 服务器地址是必填的' })} className="input" placeholder="http://localhost:32400" />
          {errors.url && <p className="mt-1 text-sm text-red-400">{errors.url.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">API 令牌 *</label>
          <input {...register('key', { required: 'API 令牌是必填的' })} className="input" placeholder="your-plex-token" />
          {errors.key && <p className="mt-1 text-sm text-red-400">{errors.key.message}</p>}
        </div>
      </div>

      {/* 功能开关配置 */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h4 className="text-lg font-medium text-gray-200 mb-4">功能配置</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableBlocks')}
              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">启用块状显示</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableNowPlaying')}
              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示正在播放</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableUser')}
              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示用户信息</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableMediaControl')}
              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">启用媒体控制</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('showEpisodeNumber')}
              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示剧集编号</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('expandOneStreamToTwoRows')}
              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">单流扩展为两行</span>
          </label>
        </div>
      </div>

      {/* 配置说明 */}
      <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-orange-300 mb-3">📖 Plex 小部件配置说明</h4>
        <div className="space-y-2 text-sm text-orange-200">
          <p><strong>块状显示</strong>：以块状布局显示媒体信息（默认关闭）</p>
          <p><strong>正在播放</strong>：显示当前正在播放的媒体（默认开启）</p>
          <p><strong>用户信息</strong>：显示用户相关信息（默认关闭）</p>
          <p><strong>媒体控制</strong>：启用播放控制按钮（默认开启）</p>
          <p><strong>剧集编号</strong>：显示电视剧的剧集编号（默认关闭）</p>
          <p><strong>单流扩展</strong>：将单个流媒体扩展为两行显示（默认开启）</p>
        </div>
      </div>
    </div>
  )

  // 🎯 qBittorrent 配置
  const renderQbittorrentConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">⬇️ qBittorrent 下载器配置</h5>

      {/* 基础配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">服务器地址 *</label>
          <input {...register('url', { required: 'qBittorrent 服务器地址是必填的' })} className="input" placeholder="http://localhost:8080" />
          {errors.url && <p className="mt-1 text-sm text-red-400">{errors.url.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">用户名</label>
          <input {...register('username')} className="input" placeholder="admin" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">密码</label>
          <input {...register('password')} type="text" className="input" />
        </div>
      </div>

      {/* 显示选项 */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h4 className="text-lg font-medium text-gray-200 mb-4">显示选项</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableQueue')}
              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示下载队列</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableActivity')}
              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示活动状态</span>
          </label>
        </div>
      </div>
    </div>
  )

  // 🎯 Transmission 配置
  const renderTransmissionConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">📡 Transmission 下载器配置</h5>

      {/* 基础配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">服务器地址 *</label>
          <input {...register('url', { required: 'Transmission 服务器地址是必填的' })} className="input" placeholder="http://localhost:9091" />
          {errors.url && <p className="mt-1 text-sm text-red-400">{errors.url.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">用户名</label>
          <input {...register('username')} className="input" placeholder="transmission" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">密码</label>
          <input {...register('password')} type="text" className="input" />
        </div>
      </div>

      {/* 显示选项 */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h4 className="text-lg font-medium text-gray-200 mb-4">显示选项</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableQueue')}
              className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示下载队列</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableActivity')}
              className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示活动状态</span>
          </label>
        </div>
      </div>
    </div>
  )

  // 🎯 *arr 系列配置 (Sonarr, Radarr, Lidarr, Readarr, Prowlarr, Bazarr)
  const renderArrConfig = (type) => {
    const typeNames = {
      sonarr: 'Sonarr (电视剧)',
      radarr: 'Radarr (电影)',
      lidarr: 'Lidarr (音乐)',
      readarr: 'Readarr (电子书)',
      prowlarr: 'Prowlarr (索引器)',
      bazarr: 'Bazarr (字幕)'
    }

    const typeColors = {
      sonarr: 'blue',
      radarr: 'yellow',
      lidarr: 'green',
      readarr: 'purple',
      prowlarr: 'orange',
      bazarr: 'cyan'
    }

    const color = typeColors[type] || 'blue'

    return (
      <div className="space-y-4">
        <h5 className="font-medium text-gray-300">🎬 {typeNames[type]} 配置</h5>

        {/* 基础配置 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">服务器地址 *</label>
            <input {...register('url', { required: `${typeNames[type]} 服务器地址是必填的` })} className="input" placeholder="http://localhost:8989" />
            {errors.url && <p className="mt-1 text-sm text-red-400">{errors.url.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">API 密钥 *</label>
            <input {...register('key', { required: 'API 密钥是必填的' })} className="input" placeholder="your-api-key" />
            {errors.key && <p className="mt-1 text-sm text-red-400">{errors.key.message}</p>}
          </div>
        </div>

        {/* 显示选项 */}
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <h4 className="text-lg font-medium text-gray-200 mb-4">显示选项</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('enableQueue')}
                className={`border-gray-600 text-${color}-600 focus:ring-${color}-500 focus:ring-offset-gray-800`}
              />
              <span className="text-sm text-gray-300">显示队列信息</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('enableActivity')}
                className={`border-gray-600 text-${color}-600 focus:ring-${color}-500 focus:ring-offset-gray-800`}
              />
              <span className="text-sm text-gray-300">显示活动状态</span>
            </label>
            {(type === 'sonarr' || type === 'radarr') && (
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('enableCalendar')}
                  className={`border-gray-600 text-${color}-600 focus:ring-${color}-500 focus:ring-offset-gray-800`}
                />
                <span className="text-sm text-gray-300">显示日历</span>
              </label>
            )}
          </div>
        </div>
      </div>
    )
  }

  // 🎯 Pi-hole 配置
  const renderPiholeConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">🕳️ Pi-hole DNS 配置</h5>

      {/* 基础配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">服务器地址 *</label>
          <input {...register('url', { required: 'Pi-hole 服务器地址是必填的' })} className="input" placeholder="http://pi.hole" />
          {errors.url && <p className="mt-1 text-sm text-red-400">{errors.url.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">API 密钥</label>
          <input {...register('key')} className="input" placeholder="your-api-key" />
        </div>
      </div>

      {/* 显示选项 */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h4 className="text-lg font-medium text-gray-200 mb-4">显示选项</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableBlocks')}
              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示统计块</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableQuery')}
              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示查询统计</span>
          </label>
        </div>
      </div>
    </div>
  )

  // 🎯 AdGuard Home 配置
  const renderAdguardConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">🛡️ AdGuard Home 配置</h5>

      {/* 基础配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">服务器地址 *</label>
          <input {...register('url', { required: 'AdGuard Home 服务器地址是必填的' })} className="input" placeholder="http://localhost:3000" />
          {errors.url && <p className="mt-1 text-sm text-red-400">{errors.url.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">用户名</label>
          <input {...register('username')} className="input" placeholder="admin" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">密码</label>
          <input {...register('password')} type="text" className="input" />
        </div>
      </div>

      {/* 显示选项 */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h4 className="text-lg font-medium text-gray-200 mb-4">显示选项</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableBlocks')}
              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示统计块</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableQuery')}
              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示查询统计</span>
          </label>
        </div>
      </div>
    </div>
  )

  // 🎯 Portainer 配置
  const renderPortainerConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">🐳 Portainer 容器管理配置</h5>

      {/* 基础配置 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">服务器地址 *</label>
          <input {...register('url', { required: 'Portainer 服务器地址是必填的' })} className="input" placeholder="http://localhost:9000" />
          {errors.url && <p className="mt-1 text-sm text-red-400">{errors.url.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">API 密钥</label>
          <input {...register('key')} className="input" placeholder="your-api-key" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">环境 ID</label>
          <input {...register('env')} className="input" placeholder="1" />
        </div>
      </div>

      {/* 显示选项 */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h4 className="text-lg font-medium text-gray-200 mb-4">显示选项</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableContainers')}
              className="border-gray-600 text-cyan-600 focus:ring-cyan-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示容器统计</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('enableStacks')}
              className="border-gray-600 text-cyan-600 focus:ring-cyan-500 focus:ring-offset-gray-800"
            />
            <span className="text-sm text-gray-300">显示堆栈信息</span>
          </label>
        </div>
      </div>
    </div>
  )

  const renderCustomApiConfig = () => (
    <div className="space-y-4">
      <h5 className="font-medium text-gray-300">🔗 自定义 API 配置</h5>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">API URL</label>
        <input {...register('url')} className="input" placeholder="http://api.example.com" />
      </div>
    </div>
  )

  useEffect(() => {
    if (isOpen && widget) {
      const widgetType = Object.keys(widget)[0]
      const widgetConfig = widget[widgetType] || {}
      setValue('type', widgetType)

      // 设置所有字段值，包括 checkbox 字段
      Object.keys(widgetConfig).forEach(key => {
        if (typeof widgetConfig[key] !== 'object') {
          setValue(key, widgetConfig[key])
        }
      })

      // 处理 datetime 的 format 对象
      if (widgetType === 'datetime' && widgetConfig.format && typeof widgetConfig.format === 'object') {
        const fmt = widgetConfig.format
        if (fmt.dateStyle) setValue('dateStyle', fmt.dateStyle)
        if (fmt.timeStyle) setValue('timeStyle', fmt.timeStyle)
        if (typeof fmt.hour12 === 'boolean') setValue('hour12', fmt.hour12)
        if (fmt.hourCycle) setValue('hourCycle', fmt.hourCycle)
      }

      // 特殊处理搜索小部件的多提供商模式
      if (widgetType === 'search' && Array.isArray(widgetConfig.provider)) {
        // 如果 provider 是数组，说明启用了多提供商模式
        setValue('enableMultiProviders', true)

        // 根据数组中的提供商设置对应的复选框
        const selectedProviders = widgetConfig.provider
        setValue('providers_google', selectedProviders.includes('google'))
        setValue('providers_duckduckgo', selectedProviders.includes('duckduckgo'))
        setValue('providers_bing', selectedProviders.includes('bing'))
        setValue('providers_baidu', selectedProviders.includes('baidu'))
        setValue('providers_brave', selectedProviders.includes('brave'))

        // 清空单一提供商选择
        setValue('provider', '')
      } else if (widgetType === 'search' && widgetConfig.provider) {
        // 单一提供商模式
        setValue('enableMultiProviders', false)
        setValue('provider', widgetConfig.provider)

        // 清空多提供商复选框
        setValue('providers_google', false)
        setValue('providers_duckduckgo', false)
        setValue('providers_bing', false)
        setValue('providers_baidu', false)
        setValue('providers_brave', false)
      }

      // 特别处理所有小部件的 checkbox 字段
      const checkboxFieldsByType = {
        search: [
          'showSearchSuggestions', 'focus', 'enableMultiProviders',
          'providers_duckduckgo', 'providers_google', 'providers_bing',
          'providers_baidu', 'providers_brave'
        ],
        resources: [
          'cpu', 'memory', 'cputemp', 'uptime', 'load', 'expanded'
        ],
        bilibili: [
          'showView', 'showLike', 'showFavorite', 'showCoin'
        ],
        emby: [
          '_nativeEmbyWidget', 'enableBlocks', 'enableNowPlaying', 'enableUser',
          'enableMediaControl', 'showEpisodeNumber', 'expandOneStreamToTwoRows'
        ],
        jellyfin: [
          'enableBlocks', 'enableNowPlaying', 'enableUser', 'enableMediaControl'
        ],
        plex: [
          'enableBlocks', 'enableNowPlaying', 'enableUser', 'enableMediaControl'
        ],
        tautulli: [
          'enableUser', 'showEpisodeNumber', 'expandOneStreamToTwoRows'
        ]
      }

      const checkboxFields = checkboxFieldsByType[widgetType] || []
      checkboxFields.forEach(field => {
        if (widgetConfig[field] !== undefined) {
          setValue(field, widgetConfig[field])
        }
      })

      // 为各种小部件设置默认的 checkbox 值
      const setDefaultCheckboxValues = (type, config) => {
        // 精简后的小部件默认值
        const defaults = {
          search: {
            showSearchSuggestions: true
          }
        }

        const typeDefaults = defaults[type]
        if (typeDefaults) {
          Object.keys(typeDefaults).forEach(key => {
            if (config[key] === undefined) {
              setValue(key, typeDefaults[key])
            }
          })
        }
      }

      setDefaultCheckboxValues(widgetType, widgetConfig)

      // 扩展所有可能的标准字段列表，包含所有小部件类型的字段
      const allStandardFields = [
        // 基本字段
        'type', 'label', 'href', 'target',
        // 服务相关
        'url', 'username', 'password', 'key', 'version', 'env',
        // 系统监控
        'cpu', 'memory', 'disk', 'cputemp', 'uptime', 'load', 'network',
        'selectedMetric', 'interface', 'refreshInterval', 'pointsLimit', 'diskUnits', 'chart',
        // 搜索相关
        'provider', 'showSearchSuggestions', 'customName', 'customUrl',
        // 天气相关
        'latitude', 'longitude', 'apiKey', 'units', 'cache',
        // 日期时间
        'text_size', 'format',
        // 问候语
        'text',
        // Logo
        'icon', 'size',
        // Resources 扩展字段
        'expanded', 'tempmin', 'tempmax', 'refresh', 'diskUnits', 'units', 'networkInterface', 'enableNetwork', 'diskInput',
        // Bilibili
        'videoId', 'apiBaseUrl', 'showView', 'showLike', 'showFavorite', 'showCoin', 'fields',
        // 媒体服务器相关
        '_nativeEmbyWidget', 'enableBlocks', 'enableNowPlaying', 'enableUser',
        'enableMediaControl', 'showEpisodeNumber', 'expandOneStreamToTwoRows',
        // 下载工具相关
        'enableQueue', 'enableActivity', 'enableCalendar',
        // 网络安全相关
        'enableQuery',
        // 容器管理相关
        'enableContainers', 'enableStacks'
      ]

      // 修复：默认不显示任何自定义字段，只有用户主动添加时才显示
      // 这解决了编辑现有小部件时自动显示“自定义字段”导致保存失败的问题
      setCustomFields([])
      setFieldIdCounter(0)
    } else if (isOpen) {
      reset()
      setCustomFields([])
      setFieldIdCounter(0)

      // 为新建小部件设置默认值（当类型改变时）
      const currentType = watch('type')
      if (currentType) {
        const setDefaultCheckboxValues = (type) => {
          const defaults = {
            emby: {
              enableNowPlaying: true,
              enableMediaControl: true,
              expandOneStreamToTwoRows: true
            },
            jellyfin: {
              enableNowPlaying: true,
              enableMediaControl: true,
              expandOneStreamToTwoRows: true
            },
            plex: {
              enableNowPlaying: true,
              enableMediaControl: true,
              expandOneStreamToTwoRows: true
            },
            qbittorrent: {
              enableQueue: true,
              enableActivity: true
            },
            transmission: {
              enableQueue: true,
              enableActivity: true
            },
            sonarr: {
              enableQueue: true,
              enableActivity: true
            },
            radarr: {
              enableQueue: true,
              enableActivity: true
            },
            lidarr: {
              enableQueue: true,
              enableActivity: true
            },
            readarr: {
              enableQueue: true,
              enableActivity: true
            },
            prowlarr: {
              enableQueue: true,
              enableActivity: true
            },
            bazarr: {
              enableQueue: true,
              enableActivity: true
            },
            pihole: {
              enableBlocks: true,
              enableQuery: true
            },
            adguardhome: {
              enableBlocks: true,
              enableQuery: true
            },
            portainer: {
              enableContainers: true
            }
          }

          const typeDefaults = defaults[type]
          if (typeDefaults) {
            Object.keys(typeDefaults).forEach(key => {
              setValue(key, typeDefaults[key])
            })
          }
        }

        setDefaultCheckboxValues(currentType)
      }
    }
  }, [isOpen, widget, setValue, reset])

  // 监听类型变化，为新选择的类型设置默认值
  useEffect(() => {
    if (isOpen && !widget && watchedType) {
      const setDefaultCheckboxValues = (type) => {
        // 精简后的小部件默认值
        const defaults = {
          search: {
            showSearchSuggestions: true
          }
        }

        const typeDefaults = defaults[type]
        if (typeDefaults) {
          Object.keys(typeDefaults).forEach(key => {
            setValue(key, typeDefaults[key])
          })
        }
      }

      setDefaultCheckboxValues(watchedType)
    }
  }, [watchedType, isOpen, widget, setValue])

  const onSubmit = (data) => {
    const { type, ...configData } = data

    const widgetTypeFields = {
      // 精简后的小部件类型字段映射 - 只保留基本类型
      resources: ['label', 'expanded', 'cpu', 'memory', 'cputemp', 'tempmin', 'tempmax', 'uptime', 'load', 'refresh', 'diskUnits', 'units', 'disk', 'network'],
      search: ['provider', 'target', 'focus', 'showSearchSuggestions', 'customUrl', 'suggestionUrl', 'enableMultiProviders', 'providers_duckduckgo', 'providers_google', 'providers_bing', 'providers_baidu', 'providers_brave'],
      datetime: ['text_size', 'locale', 'href', 'target'],
      greeting: ['text', 'text_size'],
      logo: ['icon', 'href', 'target', 'size'],
      openmeteo: ['latitude', 'longitude', 'location', 'units'],
      customapi: ['url', 'href', 'target']
    }

    const finalConfig = {}
    const validFields = widgetTypeFields[type] || []

    validFields.forEach(fieldName => {
      const value = configData[fieldName]
      if (value !== null && value !== undefined && value !== '') {
        finalConfig[fieldName] = value
      } else if (typeof value === 'boolean') {
        finalConfig[fieldName] = value
      }
    })

    // 特殊处理：日期时间小部件 -> 组装 format 对象（Intl.DateTimeFormat）
    if (type === 'datetime') {
      const formatObj = {}
      if (configData.dateStyle) formatObj.dateStyle = configData.dateStyle
      if (configData.timeStyle) formatObj.timeStyle = configData.timeStyle
      if (configData.hourCycle) formatObj.hourCycle = configData.hourCycle
      if (configData.hour12 === true) formatObj.hour12 = true
      if (Object.keys(formatObj).length > 0) {
        finalConfig.format = formatObj
      }
    }

    // 特殊处理：搜索小部件
    if (type === 'search') {
      const searchConfig = {}

      // 处理多提供商模式
      if (configData.enableMultiProviders) {
        const selectedProviders = []
        if (configData.providers_google) selectedProviders.push('google')
        if (configData.providers_duckduckgo) selectedProviders.push('duckduckgo')
        if (configData.providers_bing) selectedProviders.push('bing')
        if (configData.providers_baidu) selectedProviders.push('baidu')
        if (configData.providers_brave) selectedProviders.push('brave')

        if (selectedProviders.length > 1) {
          searchConfig.provider = selectedProviders
        } else if (selectedProviders.length === 1) {
          searchConfig.provider = selectedProviders[0]
        } else {
          // 默认为 duckduckgo
          searchConfig.provider = 'duckduckgo'
        }
      } else if (configData.provider === 'custom') {
        // 自定义搜索
        searchConfig.provider = 'custom'
        if (configData.customUrl) {
          searchConfig.url = configData.customUrl
        }
        if (configData.suggestionUrl) {
          searchConfig.suggestionUrl = configData.suggestionUrl
        }
      } else {
        // 单一提供商
        searchConfig.provider = configData.provider || 'duckduckgo'
      }

      // 其他通用配置
      if (configData.target) {
        searchConfig.target = configData.target
      }
      if (configData.focus) {
        searchConfig.focus = true
      }
      if (configData.showSearchSuggestions) {
        searchConfig.showSearchSuggestions = true
      }

      const widgetData = { [type]: searchConfig }
      onSave(widgetData, index)
      onClose()
      return
    }

    customFields.forEach(field => {
      if (field.key && field.key.trim() && field.value !== '' && field.value !== null && field.value !== undefined) {
        finalConfig[field.key.trim()] = field.value
      }
    })

    // 额外重构：resources 的特殊字段映射
    if (type === 'resources') {
      // 磁盘：支持 textarea 多行 -> 数组；单行 -> 字符串
      const diskInput = configData.diskInput
      if (diskInput && typeof diskInput === 'string') {
        const lines = diskInput
          .split(/\r?\n/)
          .map(s => s.trim())
          .filter(Boolean)
        if (lines.length === 1) {
          finalConfig.disk = lines[0]
        } else if (lines.length > 1) {
          finalConfig.disk = lines
        }
      }

      // 网络：复选框 + 可选接口名
      if (configData.enableNetwork) {
        finalConfig.network = configData.networkInterface && configData.networkInterface.trim()
          ? configData.networkInterface.trim()
          : true
      } else {
        // 未启用则不写入 network 字段
        delete finalConfig.network
      }

      // 类型修正：数字字段
      const numericKeys = ['refresh', 'tempmin', 'tempmax']
      numericKeys.forEach(k => {
        if (finalConfig[k] !== undefined && finalConfig[k] !== '') {
          const n = Number(finalConfig[k])
          if (!Number.isNaN(n)) finalConfig[k] = n
        }
      })

      // 清理表单辅助字段
      delete finalConfig.diskInput
      delete finalConfig.enableNetwork
      delete finalConfig.networkInterface
    }

    const widgetData = { [type]: finalConfig }
    onSave(widgetData, index)
    onClose()
  }

  const addCustomField = () => {
    const newField = {
      id: Date.now() + fieldIdCounter,
      key: '',
      value: '',
      isOriginal: false
    }
    setCustomFields([...customFields, newField])
    setFieldIdCounter(fieldIdCounter + 1)
  }

  const updateCustomField = (id, field, value) => {
    const newFields = customFields.map(f =>
      f.id === id ? { ...f, [field]: value } : f
    )
    setCustomFields(newFields)
  }

  const removeCustomField = (id) => {
    const newFields = customFields.filter(f => f.id !== id)
    setCustomFields(newFields)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-800 shadow-xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">
              {widget ? '编辑小部件' : '添加小部件'}
            </h3>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-200">基本信息</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">小部件类型 *</label>
                  <select {...register('type', { required: '小部件类型是必填的' })} className="select">
                    <option value="">选择类型</option>
                    <optgroup label="📊 系统监控">
                      <option value="resources">系统资源</option>
                    </optgroup>
                    <optgroup label="🔧 基本小部件">
                      <option value="search">搜索框</option>
                      <option value="datetime">日期时间</option>
                      <option value="greeting">问候语</option>
                      <option value="logo">Logo</option>
                      <option value="openmeteo">天气 (OpenMeteo)</option>
                      <option value="customapi">自定义 API</option>
                    </optgroup>
                  </select>
                  {errors.type && <p className="mt-1 text-sm text-red-400">{errors.type.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">标签</label>
                  <input {...register('label')} className="input" placeholder="小部件标签" />
                </div>
              </div>

              {watchedType && (
                <div className="space-y-6">
                  {renderTypeSpecificConfig(watchedType)}

                  {shouldShowLinkConfig(watchedType) && (
                    <div className="space-y-4">
                      <h5 className="font-medium text-gray-300">🔗 链接配置</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">链接地址</label>
                          <input {...register('href')} className="input" placeholder="https://example.com" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">打开方式</label>
                          <select {...register('target')} className="select">
                            <option value="_blank">新标签页</option>
                            <option value="_self">当前页面</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 自定义字段 */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-300">自定义字段</label>
                  <button type="button" onClick={addCustomField} className="btn-secondary btn-sm">
                    <PlusIcon className="w-4 h-4 mr-1" />
                    添加字段
                  </button>
                </div>

                {customFields.map((field) => (
                  <div key={field.id} className="flex items-center space-x-2 mb-2">
                    <input
                      value={field.key}
                      onChange={(e) => updateCustomField(field.id, 'key', e.target.value)}
                      className="input flex-1"
                      placeholder="字段名"
                    />
                    <input
                      value={field.value}
                      onChange={(e) => updateCustomField(field.id, 'value', e.target.value)}
                      className="input flex-1"
                      placeholder="字段值"
                    />
                    <button
                      type="button"
                      onClick={() => removeCustomField(field.id)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded-lg"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-700">
              <button type="button" onClick={onClose} className="btn-secondary">取消</button>
              <button type="submit" className="btn-primary">
                {widget ? '更新小部件' : '添加小部件'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default WidgetEditModal
